import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, FolderOpen } from 'lucide-react';
import { 
  API_URL, 
  groupColors, 
  getGroupIcon, 
  availableIcons, 
  availableColors, 
  initialGroupData 
} from './templateConstants';

export default function FieldGroupsTab({ 
  token, 
  fieldGroups, 
  customFields,
  onRefresh,
  onRefreshFields
}) {
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupData, setGroupData] = useState(initialGroupData);
  const [savingGroup, setSavingGroup] = useState(false);
  const [initializingGroups, setInitializingGroups] = useState(false);

  const handleInitializeGroups = async () => {
    setInitializingGroups(true);
    try {
      const res = await fetch(`${API_URL}/api/case-management/field-groups/initialize-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.groups?.length > 0) {
          toast.success(`Created ${data.groups.length} field groups!`);
          onRefresh();
        } else {
          toast.info('Default field groups already exist');
        }
      }
    } catch (error) {
      toast.error('Failed to initialize field groups');
    } finally {
      setInitializingGroups(false);
    }
  };

  const handleSaveGroup = async () => {
    if (!groupData.name) {
      toast.error('Group name is required');
      return;
    }

    setSavingGroup(true);
    try {
      const url = editingGroup 
        ? `${API_URL}/api/case-management/field-groups/${editingGroup.id}`
        : `${API_URL}/api/case-management/field-groups`;

      const res = await fetch(url, {
        method: editingGroup ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(groupData)
      });

      if (res.ok) {
        toast.success(editingGroup ? 'Group updated' : 'Group created');
        setShowGroupDialog(false);
        setEditingGroup(null);
        setGroupData(initialGroupData);
        onRefresh();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save group');
      }
    } catch (error) {
      toast.error('Failed to save group');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Delete this group? Fields in this group will become ungrouped.')) return;

    try {
      const res = await fetch(`${API_URL}/api/case-management/field-groups/${groupId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Group deleted');
        onRefresh();
        onRefreshFields();
      } else {
        toast.error('Failed to delete group');
      }
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const getFieldCountForGroup = (groupId) => {
    return customFields.filter(f => f.group_id === groupId).length;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Field Groups</CardTitle>
              <CardDescription>
                Organize custom fields into logical groups for easier management
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                onClick={handleInitializeGroups}
                disabled={initializingGroups}
              >
                {initializingGroups ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FolderOpen className="w-4 h-4 mr-2" />
                )}
                Initialize Defaults
              </Button>
              <Button 
                onClick={() => {
                  setEditingGroup(null);
                  setGroupData(initialGroupData);
                  setShowGroupDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Group
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {fieldGroups.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {fieldGroups.map((group) => {
                const IconComponent = getGroupIcon(group.icon);
                const colorClasses = groupColors[group.color] || groupColors.slate;
                const fieldCount = getFieldCountForGroup(group.id);

                return (
                  <div
                    key={group.id}
                    className={`p-4 rounded-lg border-2 ${colorClasses}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">{group.name}</p>
                          <p className="text-xs opacity-70">
                            {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingGroup(group);
                            setGroupData({
                              name: group.name,
                              description: group.description || '',
                              icon: group.icon || 'folder',
                              color: group.color || 'slate'
                            });
                            setShowGroupDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteGroup(group.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {group.description && (
                      <p className="text-xs mt-2 opacity-70">{group.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Field Groups</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Create groups to organize your custom fields (e.g., Insurance Info, Medical Info).
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" onClick={handleInitializeGroups} disabled={initializingGroups}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Initialize Defaults
                </Button>
                <Button onClick={() => {
                  setEditingGroup(null);
                  setGroupData(initialGroupData);
                  setShowGroupDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Group
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Field Group' : 'New Field Group'}</DialogTitle>
            <DialogDescription>
              Groups help organize custom fields on case detail pages
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input
                value={groupData.name}
                onChange={(e) => setGroupData({ ...groupData, name: e.target.value })}
                placeholder="e.g., Insurance Information"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={groupData.description}
                onChange={(e) => setGroupData({ ...groupData, description: e.target.value })}
                placeholder="Brief description of this group"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={groupData.icon}
                  onValueChange={(value) => setGroupData({ ...groupData, icon: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableIcons.map((icon) => (
                      <SelectItem key={icon.value} value={icon.value}>
                        {icon.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={groupData.color}
                  onValueChange={(value) => setGroupData({ ...groupData, color: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableColors.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full bg-${color.value}-500`} />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Preview */}
            <div className="pt-2 border-t">
              <Label className="text-xs text-muted-foreground">Preview</Label>
              <div className={`mt-2 p-3 rounded-lg border-2 ${groupColors[groupData.color] || groupColors.slate}`}>
                <div className="flex items-center gap-2">
                  {(() => {
                    const IconComp = getGroupIcon(groupData.icon);
                    return <IconComp className="w-5 h-5" />;
                  })()}
                  <span className="font-medium">{groupData.name || 'Group Name'}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup} disabled={savingGroup}>
              {savingGroup && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingGroup ? 'Update' : 'Create'} Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
